import { connectivityService } from '~/data/connectivity/ConnectivityService';
import { documentLocal, specificationLocal } from '~/data/local';
import { documentationRemote } from '~/data/remote';
import { Archive, ArchivePayload, Equipment, MachineSpecification } from '~/types';
import store from '~/store';

export class DocumentationRepository {
  async loadSpecifications(id: MachineSpecification['id']): Promise<MachineSpecification | undefined> {
    if (connectivityService.isOnline()) {
      const data = await documentationRemote.fetchSpecifications(id);
      if (store.getState().machines.isOfflineAvailable(id)) {
        await specificationLocal.updateMachineSpecifications(data);
      }
      return data;
    }
    return specificationLocal.getMachineSpecifications(id);
  }

  async loadEquipments(equipmentId: Equipment['id']): Promise<Equipment[]> {
    if (connectivityService.isOnline()) {
      return documentationRemote.fetchEquipments(equipmentId);
    }
    return (await specificationLocal.getMachineEquipments(equipmentId)) || [];
  }

  async fetchAndCacheFavoriteSpecifications(): Promise<MachineSpecification[]> {
    if (!connectivityService.isOnline()) {
      return [];
    }
    const data = await documentationRemote.fetchFavoriteSpecifications();
    await this.cacheSpecifications(data);
    return data;
  }

  async loadArchives(payload: ArchivePayload): Promise<Archive[]> {
    if (connectivityService.isOnline()) {
      const data = await documentationRemote.fetchArchives(payload);
      if (store.getState().machines.isOfflineAvailable(payload.machineId)) {
        await documentLocal.reset(data, payload.machineId, payload.parentFolderId);
      }
      return data;
    }
    return (await documentLocal.getDocuments(payload.machineId, payload.parentFolderId)) || [];
  }

  async cacheSpecifications(machines: MachineSpecification[]): Promise<void> {
    if (!machines.length) {
      return;
    }
    await specificationLocal.reset(machines);
  }

  async getEquipments(machineId: string | number) {
    return specificationLocal.getMachineEquipments(machineId);
  }

  async getSpecification(id: string | number) {
    return specificationLocal.getMachineSpecifications(id);
  }

  async updateSpecification(spec: MachineSpecification): Promise<void> {
    await specificationLocal.updateMachineSpecifications(spec);
  }

  async cacheDocuments(documents: Archive[], machineId: string, parentId: number): Promise<void> {
    await documentLocal.reset(documents, machineId, parentId);
  }

  async getDocuments(machineId: string, parentId: number): Promise<Archive[]> {
    return (await documentLocal.getDocuments(machineId, parentId)) || [];
  }

  isOnline(): boolean {
    return connectivityService.isOnline();
  }

  updateDownloadedDocument(documentId: string, filePath: string): void {
    documentLocal.updateDocument(documentId, filePath);
  }
}

export const documentationRepository = new DocumentationRepository();
